import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import Nav from '../components/Nav';

export default function Practice() {
  const nav = useNavigate();
  const location = useLocation();
  const [scenarios, setScenarios] = useState([]);
  const [usage, setUsage] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');

  useEffect(() => {
    Promise.all([api.getScenarios(), api.getUsage()])
      .then(([s, u]) => {
        setScenarios(s);
        setUsage(u);
        setSelectedId(prev => {
          if (prev) return prev;
          return location.state?.scenarioId ?? (s.length > 0 ? s[0].id : null);
        });
      }).catch(() => {});
  }, []);

  async function startCall() {
    if (!selectedId || !phone) return;
    setStatus('calling');
    try {
      const res = await api.startCall(selectedId, phone);
      setStatus('active');
      setMessage(res.message);
    } catch (err) {
      setStatus('error');
      setMessage(err.message);
    }
  }

  const atLimit = usage && usage.calls_remaining <= 0;
  const selected = scenarios.find(s => s.id === selectedId);

  return (
    <div style={S.page}>
      <Nav />
      <div style={S.content}>
        <div style={S.pageHeader}>
          <div style={S.pageTitle}>Practice Call</div>
          <div style={S.pageSub}>Select a scenario, enter your number, and we'll call you.</div>
        </div>

        {usage && (
          <div style={{ ...S.usageBanner, ...(atLimit ? S.usageBannerDanger : {}) }}>
            {atLimit
              ? `You've used all ${usage.calls_limit} calls this month. Upgrade for unlimited calls.`
              : `${usage.calls_remaining} of ${usage.calls_limit} calls remaining · Max ${usage.max_call_duration_minutes} min per call`
            }
          </div>
        )}

        {status === 'idle' && (
          <div style={S.layout}>
            <div style={S.scenarioCol}>
              <div style={S.colLabel}>Choose a scenario</div>
              {scenarios.length === 0 ? (
                <div style={S.emptyScenarios}>
                  No scenarios yet.{' '}
                  <span style={S.link} onClick={() => nav('/scenarios')}>Create one first →</span>
                </div>
              ) : (
                <div style={S.scenarioList}>
                  {scenarios.map(s => (
                    <div key={s.id}
                      style={{ ...S.scenarioItem, ...(selectedId === s.id ? S.scenarioSelected : {}) }}
                      onClick={() => setSelectedId(s.id)}>
                      <div style={S.scenarioName}>{s.name}</div>
                      <div style={S.scenarioMeta}>{s.persona_name} · {s.company_name}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={S.callCol}>
              {selected && (
                <div style={S.personaPreview}>
                  <div style={S.previewLabel}>You'll be speaking with</div>
                  <div style={S.previewName}>{selected.persona_name}</div>
                  <div style={S.previewTitle}>{selected.persona_title} at {selected.company_name}</div>
                  {selected.key_objections.length > 0 && (
                    <div style={S.previewObjections}>
                      <div style={S.objLabel}>Expect objections like:</div>
                      {selected.key_objections.slice(0, 2).map((o, i) => (
                        <div key={i} style={S.objItem}>"{o}"</div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div style={S.phoneCard}>
                <div style={S.colLabel}>Your phone number</div>
                <input
                  style={S.phoneInput}
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  value={phone}
                  onChange={e => setPhone(e.target.value)}
                />
                <div style={S.phoneNote}>We'll call this number. Answer and start your pitch.</div>
                <button
                  style={{ ...S.callBtn, ...(atLimit || !phone || !selectedId ? S.callBtnDisabled : {}) }}
                  onClick={startCall}
                  disabled={atLimit || !phone || !selectedId}>
                  📞 Call Me Now
                </button>
              </div>

              <div style={S.tipsCard}>
                <div style={S.tipsLabel}>Quick tips</div>
                <div style={S.tip}>Lead with a pain point, not a product feature</div>
                <div style={S.tip}>Your talk ratio should be under 43% (Gong)</div>
                <div style={S.tip}>Always ask for a specific next step before you hang up</div>
              </div>
            </div>
          </div>
        )}

        {status === 'calling' && (
          <div style={S.statusCard}>
            <div style={S.statusIcon}>📳</div>
            <div style={S.statusTitle}>Calling you now...</div>
            <div style={S.statusSub}>Your phone should ring within a few seconds. Answer and start pitching.</div>
          </div>
        )}

        {status === 'active' && (
          <div style={S.statusCard}>
            <div style={S.statusIcon}>🎙️</div>
            <div style={S.statusTitle}>Call in progress</div>
            <div style={S.statusSub}>{message}</div>
            <button style={S.doneBtn} onClick={() => { setStatus('idle'); nav('/dashboard'); }}>
              View my scorecard →
            </button>
          </div>
        )}

        {status === 'error' && (
          <div style={S.statusCard}>
            <div style={S.errorMsg}>{message}</div>
            <button style={S.callBtn} onClick={() => setStatus('idle')}>Try again</button>
          </div>
        )}
      </div>
    </div>
  );
}

const S = {
  page: { minHeight: '100vh', background: '#F8F9FB', fontFamily: 'Inter, -apple-system, sans-serif' },
  content: { maxWidth: 900, margin: '0 auto', padding: '32px 24px' },
  pageHeader: { marginBottom: 24 },
  pageTitle: { fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 4 },
  pageSub: { fontSize: 14, color: '#9CA3AF' },
  usageBanner: { fontSize: 13, padding: '10px 16px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 8, color: '#1D4ED8', marginBottom: 24 },
  usageBannerDanger: { background: '#FEF2F2', border: '1px solid #FECACA', color: '#B91C1C' },
  layout: { display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' },
  scenarioCol: { flex: '1 1 260px' },
  callCol: { flex: '1 1 320px', display: 'flex', flexDirection: 'column', gap: 16 },
  colLabel: { fontSize: 11, fontWeight: 600, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 },
  emptyScenarios: { fontSize: 14, color: '#9CA3AF' },
  link: { color: '#2563EB', cursor: 'pointer', fontWeight: 500 },
  scenarioList: { display: 'flex', flexDirection: 'column', gap: 8 },
  scenarioItem: { padding: '12px 16px', background: '#fff', border: '1.5px solid #E5E7EB', borderRadius: 10, cursor: 'pointer', transition: 'border-color 0.15s' },
  scenarioSelected: { borderColor: '#2563EB', background: '#EFF6FF' },
  scenarioName: { fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 2 },
  scenarioMeta: { fontSize: 12, color: '#9CA3AF' },
  personaPreview: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: '16px 20px' },
  previewLabel: { fontSize: 11, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 },
  previewName: { fontSize: 18, fontWeight: 700, color: '#111827', marginBottom: 2 },
  previewTitle: { fontSize: 13, color: '#6B7280', marginBottom: 12 },
  previewObjections: {},
  objLabel: { fontSize: 11, color: '#9CA3AF', marginBottom: 6 },
  objItem: { fontSize: 13, color: '#4B5563', padding: '6px 10px', background: '#F8F9FB', borderRadius: 6, marginBottom: 4, fontStyle: 'italic' },
  phoneCard: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: '20px' },
  phoneInput: { width: '100%', padding: '11px 14px', background: '#F8F9FB', border: '1px solid #D1D5DB', borderRadius: 8, color: '#111827', fontSize: 15, boxSizing: 'border-box', marginBottom: 8, outline: 'none' },
  phoneNote: { fontSize: 12, color: '#9CA3AF', marginBottom: 16 },
  callBtn: { width: '100%', padding: '13px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 15, cursor: 'pointer' },
  callBtnDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  tipsCard: { background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 12, padding: '16px 20px' },
  tipsLabel: { fontSize: 11, fontWeight: 600, color: '#D97706', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 },
  tip: { fontSize: 13, color: '#92400E', padding: '5px 0', borderBottom: '1px solid #FDE68A', lineHeight: 1.5 },
  statusCard: { background: '#fff', border: '1px solid #E5E7EB', borderRadius: 14, padding: '48px 32px', textAlign: 'center', maxWidth: 460, margin: '0 auto' },
  statusIcon: { fontSize: 40, marginBottom: 16 },
  statusTitle: { fontSize: 20, fontWeight: 700, color: '#111827', marginBottom: 8 },
  statusSub: { fontSize: 14, color: '#9CA3AF', marginBottom: 24 },
  doneBtn: { padding: '11px 28px', background: '#2563EB', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' },
  errorMsg: { fontSize: 14, color: '#DC2626', background: '#FEF2F2', borderRadius: 8, padding: '12px', marginBottom: 16 },
};
